import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/apiService';

const STATUS_LABELS = {
  neu: 'Neu',
  kontaktiert: 'Kontaktiert',
  qualifiziert: 'Qualifiziert',
  verloren: 'Verloren',
  konvertiert: 'Konvertiert',
};

const ProspectList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/prospects', {
          params: { q: query || undefined, status: status || undefined, page, limit: 25 },
        });
        setRows(response.data.rows || []);
        setPages(response.data.pages || 1);
      } catch (requestError) {
        setRows([]);
        setError(requestError.response?.data?.error || 'Interessenten konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [query, status, page]);

  const remove = async prospect => {
    if (!window.confirm(`Interessent „${prospect.company}“ wirklich löschen?`)) return;
    try {
      await api.delete(`/prospects/${prospect.id}`);
      setRows(current => current.filter(row => row.id !== prospect.id));
    } catch (requestError) {
      alert(requestError.response?.data?.error || 'Interessent konnte nicht gelöscht werden.');
    }
  };

  return (
    <div>
      <Helmet><title>Interessenten</title></Helmet>
      <h2>Interessentenliste</h2>

      <Link to="/prospects/new">
        <button type="button" style={{ marginBottom: '15px' }}>
          Neuen Interessenten anlegen
        </button>
      </Link>

      <div style={{ marginBottom: '20px' }}>
        <h3>Filter</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
          <input
            value={query}
            onChange={event => { setQuery(event.target.value); setPage(1); }}
            placeholder="Firma, Name, Ort oder E-Mail"
            style={{ margin: 0 }}
          />
          <select
            value={status}
            onChange={event => { setStatus(event.target.value); setPage(1); }}
            style={{ margin: 0 }}
          >
            <option value="">Alle Status</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div style={{ color: '#a00', marginBottom: 12 }}>{error}</div>}
      {loading ? <div>Lade Interessenten …</div> : (
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Firma</th>
              <th>Ort</th>
              <th>Status</th>
              <th>Herkunft</th>
              <th>Verantwortlich</th>
              <th>Ansprechpartner</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(prospect => (
              <tr key={prospect.id}>
                <td>I-{prospect.id}</td>
                <td>{prospect.company}</td>
                <td>{prospect.city || '-'}</td>
                <td>{STATUS_LABELS[prospect.status] || prospect.status}</td>
                <td>{prospect.origin?.name || '-'}</td>
                <td>
                  {prospect.responsible
                    ? `${prospect.responsible.firstName || ''} ${prospect.responsible.lastName || ''}`.trim()
                    : '-'}
                </td>
                <td>{prospect.contacts?.length || 0}</td>
                <td>
                  <Link to={`/prospects/${prospect.id}/edit`} style={{ marginRight: 10 }}>
                    Bearbeiten
                  </Link>
                  {prospect.status !== 'konvertiert' ? (
                    <>
                      <button
                        onClick={() => navigate(`/prospects/${prospect.id}/convert`)}
                        style={convertButton}
                      >
                        In Kunden umwandeln
                      </button>
                      <button onClick={() => remove(prospect)} style={deleteButton}>
                        Löschen
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => navigate(`/customerform/${prospect.convertedCustomerId}`)}
                      style={secondaryButton}
                    >
                      Kunde {prospect.convertedCustomer?.customerNumber || ''}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: 24 }}>Keine Interessenten gefunden.</td></tr>
            )}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '1rem' }}>
        <button
          disabled={page <= 1}
          onClick={() => setPage(value => value - 1)}
          style={paginationButton}
        >
          Vorherige
        </button>
        <span style={{ margin: '0 1rem' }}>Seite {page} von {pages}</span>
        <button
          disabled={page >= pages}
          onClick={() => setPage(value => value + 1)}
          style={paginationButton}
        >
          Nächste
        </button>
      </div>
    </div>
  );
};

const secondaryButton = {
  background: '#3b82f6', color: '#fff', border: 0, borderRadius: 4, padding: '5px 9px', marginRight: 6, cursor: 'pointer',
};
const convertButton = {
  ...secondaryButton, background: '#16803b',
};
const deleteButton = {
  ...secondaryButton, background: '#b42318',
};
const paginationButton = {
  fontSize: '15px',
  padding: '8px 12px',
  backgroundColor: 'rgb(155, 155, 155)',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
};

export default ProspectList;
